import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";
import { Section, Eyebrow } from "@/components/PageParts";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { PoweredByEagle } from "@/components/Brand";
import {
  Network, Users, LineChart, Bot, ShieldCheck, Building2, HeartHandshake, Calculator,
  ArrowRight, GraduationCap, ClipboardList, TrendingUp, CheckCircle2,
} from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1765896387398-1e1ae8d2eb85?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxzZW5pb3IlMjBoZWFsdGhjYXJlJTIwY2FyZWdpdmVyJTIwbnVyc2UlMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzkwNzIyMjMyfDA&ixlib=rb-4.1.0&q=85";
const MEET = "https://images.unsplash.com/photo-1758518730083-4c12527b6742?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxoZWFsdGhjYXJlJTIwZXhlY3V0aXZlJTIwb2ZmaWNlJTIwbWVldGluZ3xlbnwwfHx8fDE3OTA3MjIyNDB8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const settings = useSettings();
  const nav = useNavigate();

  return (
    <div>
      {/* Hero */}
      <div className="relative overflow-hidden bg-navy">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="relative cpp-hero-overlay">
          <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:py-32">
            <div className="max-w-3xl cpp-fade-up">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-clinical">Care Placement Pro</p>
              <h1 className="mt-5 font-heading text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
                Build a private-pay healthcare placement business
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-300">
                Learn to identify private-pay care needs, develop provider networks, build professional referral
                relationships, and use AI to automate business development — an institutional platform powered by the
                real-world healthcare operating experience of Eagle Healthcare Services, Inc.
              </p>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <Button data-testid="hero-join-btn" size="lg" onClick={() => nav("/join")}
                  className="bg-clinical text-base hover:bg-clinical/90">
                  Join Care Placement Pro — {settings.membership_price}
                </Button>
                <Button data-testid="hero-accelerator-btn" size="lg" variant="outline"
                  onClick={() => nav("/business-in-a-box")}
                  className="border-white/30 bg-transparent text-base text-white hover:bg-white/10">
                  Business-in-a-Box — {settings.accelerator_price}
                </Button>
              </div>
              <p className="mt-8 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
                Powered by Eagle Healthcare Services, Inc.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Trust strip */}
      <Section className="py-14">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            [ShieldCheck, "Institutional & compliant", "Private-pay focus with built-in compliance gates."],
            [Network, "Provider networks", "Build relationships across many service categories."],
            [Bot, "AI-first automation", "Reduce repetitive business-development work."],
            [GraduationCap, "Real operating experience", "Powered by Eagle Healthcare Services, Inc."],
          ].map(([Icon, t, d]) => (
            <div key={t} className="flex gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-sky">
                <Icon className="h-5 w-5 text-clinical" />
              </div>
              <div>
                <p className="font-heading text-sm font-semibold text-navy">{t}</p>
                <p className="mt-1 text-sm text-slate-500">{d}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Two paths */}
      <div className="bg-slate-50 py-20">
        <Section>
          <div className="max-w-2xl">
            <Eyebrow>Two paths</Eyebrow>
            <h2 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-navy lg:text-4xl">
              Choose how you want to grow
            </h2>
          </div>
          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            <PathCard icon={Users} title="Placement Professional" desc="Learn the system and apply for approved independent placement opportunities with participating providers."
              points={["Structured curriculum & scripts", "CRM, pipeline & attribution", "AI assistant for outreach", "Commission opportunity center"]}
              cta="Become a Placement Professional" to="/become-a-placement-professional" nav={nav} tid="path-professional" />
            <PathCard icon={Building2} title="Business Owner" desc="Learn to create an independent placement company, develop your own provider network, and operate independently."
              points={["Build your own provider network", "Develop referral sources", "Lawful provider agreements", "Business-in-a-Box Accelerator"]}
              cta="Build Your Own Business" to="/build-your-business" nav={nav} tid="path-owner" dark />
          </div>
        </Section>
      </div>

      {/* How it works */}
      <Section className="py-20">
        <div className="max-w-2xl">
          <Eyebrow>How it works</Eyebrow>
          <h2 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-navy lg:text-4xl">
            From learning to placement
          </h2>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-4">
          {[
            [GraduationCap, "Learn", "Master the private-pay placement model through structured training."],
            [Network, "Build networks", "Develop participating providers and professional referral sources."],
            [ClipboardList, "Match & follow up", "Run compliant intake, match to available care capacity, follow up."],
            [TrendingUp, "Get paid", "Approved written agreements define compensation on qualifying transactions."],
          ].map(([Icon, t, d], i) => (
            <Card key={t} className="p-6">
              <div className="flex items-center gap-3">
                <span className="font-heading text-3xl font-bold text-slate-200">{i + 1}</span>
                <Icon className="h-6 w-6 text-clinical" />
              </div>
              <h3 className="mt-4 font-heading text-lg font-semibold text-navy">{t}</h3>
              <p className="mt-2 text-sm text-slate-500">{d}</p>
            </Card>
          ))}
        </div>
      </Section>

      {/* Services */}
      <div className="bg-navy py-20 text-white">
        <Section>
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-clinical">Not just assisted living</p>
              <h2 className="mt-3 font-heading text-3xl font-semibold tracking-tight lg:text-4xl">
                Many private-pay care & support services
              </h2>
              <p className="mt-5 text-slate-300">
                The platform is built around available private-pay care capacity across residential and non-residential
                services — supporting hundreds or thousands of independent providers.
              </p>
              <div className="mt-7 grid grid-cols-2 gap-y-2.5 text-sm text-slate-200">
                {["Assisted Living", "Memory Care", "Family Care Homes", "Independent Living", "Private-Duty Home Care",
                  "Personal & Companion Care", "Residential & In-Home Respite", "Adult Day Services", "Care Management",
                  "Senior Transition", "Private Transportation", "Behavioral / Community Support"].map((s) => (
                  <div key={s} className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-emeraldbrand" /> {s}
                  </div>
                ))}
              </div>
              <Button data-testid="home-services-btn" onClick={() => nav("/services")} className="mt-8 bg-clinical hover:bg-clinical/90">
                Explore all services <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </div>
            <img src={MEET} alt="Healthcare professionals" className="rounded-xl object-cover shadow-2xl" />
          </div>
        </Section>
      </div>

      {/* Calculator teaser */}
      <Section className="py-20">
        <Card className="grid gap-8 overflow-hidden p-8 lg:grid-cols-2 lg:p-12">
          <div>
            <Eyebrow>Commission economics</Eyebrow>
            <h2 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-navy">
              Understand how placement economics can work
            </h2>
            <p className="mt-4 text-slate-500">
              Every provider and service category can have a different compensation formula. Use the interactive
              calculator to explore illustrative scenarios — clearly labeled as estimates, never guarantees.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button data-testid="home-calculator-btn" onClick={() => nav("/commission-calculator")} className="bg-navy hover:bg-navy/90">
                <Calculator className="mr-2 h-4 w-4" /> Open commission calculator
              </Button>
              <Button variant="outline" data-testid="home-opps-btn" onClick={() => nav("/commission-opportunities")}>
                View opportunities
              </Button>
            </div>
          </div>
          <div className="rounded-xl bg-slate-50 p-8">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Illustrative — not guaranteed</p>
            <div className="mt-4 space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Monthly resident charge</span><span className="font-semibold text-navy">$10,000</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Hypothetical placement fee</span><span className="font-semibold text-navy">100% first month</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Hypothetical agent share</span><span className="font-semibold text-navy">50%</span></div>
              <div className="mt-3 flex justify-between rounded-lg bg-emeraldbrand/10 p-3">
                <span className="font-semibold text-emeraldbrand">Illustrative potential commission</span>
                <span className="font-heading text-xl font-bold text-emeraldbrand">$5,000</span>
              </div>
            </div>
          </div>
        </Card>
      </Section>

      {/* CTA */}
      <div className="bg-slate-50 py-20">
        <Section>
          <Card className="flex flex-col items-center gap-6 bg-navy p-12 text-center text-white">
            <HeartHandshake className="h-10 w-10 text-clinical" />
            <h2 className="font-heading text-3xl font-semibold tracking-tight lg:text-4xl">
              Start building your placement business
            </h2>
            <p className="max-w-2xl text-slate-300">
              Join the Care Placement Pro membership for {settings.membership_price}, or get the full machine with the
              Business-in-a-Box Accelerator.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button data-testid="cta-join-btn" size="lg" onClick={() => nav("/join")} className="bg-clinical hover:bg-clinical/90">
                Join — {settings.membership_price}
              </Button>
              <Button data-testid="cta-accelerator-btn" size="lg" variant="outline" onClick={() => nav("/business-in-a-box")}
                className="border-white/30 bg-transparent text-white hover:bg-white/10">
                Business-in-a-Box — {settings.accelerator_price}
              </Button>
            </div>
            <PoweredByEagle className="mt-2" />
          </Card>
        </Section>
      </div>
    </div>
  );
}

function PathCard({ icon: Icon, title, desc, points, cta, to, nav, tid, dark }) {
  return (
    <Card className={`flex flex-col p-8 ${dark ? "bg-navy text-white" : ""}`} data-testid={tid}>
      <div className={`flex h-12 w-12 items-center justify-center rounded-lg ${dark ? "bg-clinical" : "bg-sky"}`}>
        <Icon className={`h-6 w-6 ${dark ? "text-white" : "text-clinical"}`} />
      </div>
      <h3 className={`mt-5 font-heading text-2xl font-semibold ${dark ? "text-white" : "text-navy"}`}>{title}</h3>
      <p className={`mt-3 ${dark ? "text-slate-300" : "text-slate-500"}`}>{desc}</p>
      <ul className="mt-5 space-y-2.5">
        {points.map((p) => (
          <li key={p} className="flex items-center gap-2 text-sm">
            <CheckCircle2 className={`h-4 w-4 shrink-0 ${dark ? "text-emeraldbrand" : "text-emeraldbrand"}`} />
            <span className={dark ? "text-slate-200" : "text-slate-600"}>{p}</span>
          </li>
        ))}
      </ul>
      <Button data-testid={`${tid}-btn`} onClick={() => nav(to)}
        className={`mt-7 w-full ${dark ? "bg-clinical hover:bg-clinical/90" : "bg-navy hover:bg-navy/90"}`}>
        {cta} <ArrowRight className="ml-1.5 h-4 w-4" />
      </Button>
    </Card>
  );
}
